import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Card, CardContent, Typography, Avatar, Divider,
    CircularProgress, Alert, Grid, IconButton, Chip
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import GroupIcon from "@mui/icons-material/Group";
import StarIcon from "@mui/icons-material/Star";
import useDataFetch, { usePostDataFetch } from "../../utils/useDataFetch";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useAuth } from '../../context/AuthContext';

const PublicProfile = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: loggedInUser } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const { data: profileResp, error, isLoading, refetch } = useDataFetch({ url: `/users/profile/${userId}` });
    const { postData: submitComment, data: commentData, isLoading: isCommenting } = usePostDataFetch();

    const [newComment, setNewComment] = useState("");

    useEffect(() => {
        if (commentData?.success) {
            setNewComment("");
            refetch();
        }
    }, [commentData]);

    const handleAddComment = (e) => {
        e.preventDefault();
        submitComment({ url: `/users/profile/${userId}/comment`, aBody: { kommentar: newComment }, skipAuth: false });
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Kommentar wirklich löschen?")) return;
        try {
            const token = localStorage.getItem("token");
            await fetch(`${apiUrl}/users/comment/${commentId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            refetch();
        } catch (err) {
            console.error(err);
        }
    };

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!profileResp?.user) return <Alert severity="warning">Profil nicht gefunden</Alert>;

    const { user, teams, comments } = profileResp;
    const imageUrl = user?.profilbild_url ? `${apiUrl}${user.profilbild_url}` : "";
    const isOwnProfile = loggedInUser?.id === parseInt(userId);

    const positionColor = {
        Torwart: "#F59E0B",
        Verteidiger: "#3B82F6",
        Mittelfeld: "#8B5CF6",
        Sturm: "#EF4444",
        Trainer: "#10B981",
    }[user?.bevorzugte_position] || "#6B7280";

    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}` || 'SG';
    const displayName = `${firstName} ${lastName}`.trim() || 'Unbekannt';

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 900, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton onClick={() => navigate(-1)} size="small">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Spielerprofil
                </Typography>
            </Box>

            {/* Profil-Card */}
            <Card sx={{ borderRadius: "12px", boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                <CardContent>
                    <Grid container spacing={4}>
                        {/* Avatar & Name */}
                        <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                                src={imageUrl}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    fontSize: "2rem",
                                    bgcolor: "primary.main",
                                    border: "3px solid",
                                    borderColor: "primary.light",
                                }}
                            >
                                {!imageUrl && initials}
                            </Avatar>

                            <Typography variant="h5" sx={{ fontWeight: 800, textAlign: "center" }}>
                                {displayName}
                            </Typography>

                            <Chip
                                label={user?.role?.toUpperCase() || 'N/A'}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />

                            {user?.bevorzugte_position && (
                                <Chip
                                    icon={<SportsSoccerIcon sx={{ fontSize: "0.9rem !important" }} />}
                                    label={user.bevorzugte_position}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: `${positionColor}22`,
                                        color: positionColor,
                                        borderColor: positionColor,
                                        border: "1px solid",
                                    }}
                                />
                            )}

                            {isOwnProfile && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    Das bist du
                                </Typography>
                            )}
                        </Grid>

                        {/* Infos */}
                        <Grid >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <InfoRow label="Über mich" value={user?.biografie} />
                                <InfoRow label="Starker Fuß" value={user?.starker_fuss} />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Kommentare */}
                {/* <Grid >
                    <Card sx={{ height: "100%", borderRadius: "12px", boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Kommentare</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {!user?.kommentare_erlaubt ? (
                                <Typography variant="body2" color="text.secondary">
                                    Kommentare sind für dieses Profil deaktiviert.
                                </Typography>
                            ) : (
                                <Box>
                                    <Box component="form" onSubmit={handleAddComment} sx={{ display: "flex", gap: 1, mb: 2 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Schreibe einen Kommentar..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            required
                                            sx={{ border: "1px solid #f2b903" }}
                                        />
                                        <Button variant="contained" type="submit" disabled={isCommenting} size="small">
                                            Senden
                                        </Button>
                                    </Box>

                                    {comments && comments.length > 0 ? (
                                        comments.map((c) => (
                                            <Box key={c.id} sx={{
                                                mb: 1.5, p: 1.5,
                                                bgcolor: "background.default",
                                                borderRadius: "12px",
                                                border: "1px solid", borderColor: "divider"
                                            }}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {c.first_name} {c.last_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(c.created_at).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                    {c.kommentar}
                                                </Typography>
                                                {(c.autor_id === loggedInUser?.id || parseInt(userId) === loggedInUser?.id) && (
                                                    <Button
                                                        size="small" color="error"
                                                        sx={{ minWidth: 0, p: 0, mt: 1 }}
                                                        onClick={() => handleDeleteComment(c.id)}
                                                    >
                                                        Löschen
                                                    </Button>
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Noch keine Kommentare vorhanden.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid> */}
            </Grid>
        </Box>
    );
};

const InfoRow = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.25 }}>
            {value || <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>–</span>}
        </Typography>
    </Box>
);

export default PublicProfile;